import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  getDiwaliSeasonInfo,
  DiwaliSeasonInfo,
} from '../utils/diwaliTheme';
import {
  subscribeToGlobalDiwaliTheme,
  setGlobalDiwaliTheme,
  getInitialGlobalDiwaliState,
} from '../services/systemThemeService';

export type Theme = 'light' | 'dark';

export interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  isDiwali: boolean;
  isDiwaliSeason: boolean;
  toggleDiwali: (adminUser?: { email?: string | null; uid?: string } | null) => void;
  setDiwali: (enabled: boolean, adminUser?: { email?: string | null; uid?: string } | null) => void;
  diwaliSeasonInfo: DiwaliSeasonInfo;
}

const THEME_STORAGE_KEY = 'niftysutra-theme';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }

    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch {
    // ignore localStorage/mediaQuery errors
  }

  return 'light';
};

const applyThemeToDOM = (theme: Theme, isDiwali: boolean) => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const body = document.body;

  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme;

  body.setAttribute('data-theme', theme);

  if (theme === 'dark') {
    body.classList.add('ns-theme-dark');
    body.classList.remove('ns-theme-light');
  } else {
    body.classList.add('ns-theme-light');
    body.classList.remove('ns-theme-dark');
  }

  // Apply or remove Diwali festive theme styling
  if (isDiwali) {
    root.setAttribute('data-diwali', 'true');
    body.setAttribute('data-diwali', 'true');
    body.classList.add('ns-theme-diwali');
  } else {
    root.removeAttribute('data-diwali');
    body.removeAttribute('data-diwali');
    body.classList.remove('ns-theme-diwali');
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [isDiwali, setIsDiwaliState] = useState<boolean>(getInitialGlobalDiwaliState);
  const diwaliSeasonInfo = useMemo(() => getDiwaliSeasonInfo(), []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch {
      // ignore localStorage errors
    }
    applyThemeToDOM(newTheme, isDiwali);
  }, [isDiwali]);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const nextTheme: Theme = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      } catch {
        // ignore localStorage errors
      }
      applyThemeToDOM(nextTheme, isDiwali);
      return nextTheme;
    });
  }, [isDiwali]);

  const setDiwali = useCallback((enabled: boolean, adminUser?: { email?: string | null; uid?: string } | null) => {
    setIsDiwaliState(enabled);
    applyThemeToDOM(theme, enabled);
    void setGlobalDiwaliTheme(enabled, adminUser);
  }, [theme]);

  const toggleDiwali = useCallback((adminUser?: { email?: string | null; uid?: string } | null) => {
    setIsDiwaliState((prev) => {
      const next = !prev;
      applyThemeToDOM(theme, next);
      void setGlobalDiwaliTheme(next, adminUser);
      return next;
    });
  }, [theme]);

  // Apply theme on mount and changes
  useEffect(() => {
    applyThemeToDOM(theme, isDiwali);
  }, [theme, isDiwali]);

  // Real-time synchronization: listen to global Firestore updates & cross-tab events
  useEffect(() => {
    const unsubscribe = subscribeToGlobalDiwaliTheme((globalOverride) => {
      if (globalOverride !== null) {
        setIsDiwaliState(globalOverride);
      } else {
        setIsDiwaliState(getDiwaliSeasonInfo().isSeason);
      }
    });

    return unsubscribe;
  }, []);

  // Listen to system preference changes if user hasn't explicitly set a preference
  useEffect(() => {
    if (!window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        if (!stored) {
          setTheme(e.matches ? 'dark' : 'light');
        }
      } catch {
        // ignore
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [setTheme]);

  const value = {
    theme,
    isDark: theme === 'dark',
    toggleTheme,
    setTheme,
    isDiwali,
    isDiwaliSeason: diwaliSeasonInfo.isSeason,
    toggleDiwali,
    setDiwali,
    diwaliSeasonInfo,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
